import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { PortableText } from '@portabletext/react';
import ServicesDetail from './ServicesDetail';
import ServicesOther from './ServicesOther';
import GetInTouch from "../GetInTouch/GetInTouch";
import ServiceKeypoint from './ServiceKeypoint';
import sanityClient from '../../sanityClient';
import { useDispatch, useSelector } from 'react-redux';
import { setServiceDetailPage, setServiceMobile, setNameStickyService } from '../../ReduxStore/serviceSlice';

export default function Services() {
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [relatedProjects, setRelatedProjects] = useState([]); // Add relatedProjects state
  const { slug } = useParams();
  // const [nameStickyService, setNameStickyService] = useState(false);
  // const [isServiceMobile, setIsServiceMobile] = useState(false);
  // console.log(isServiceMobile , "isServiceMobile>>>>");
  // console.log(nameStickyService , "nameStickyService>>>>");

  const dispatch = useDispatch();
  const navigate = useNavigate();


  const isServiceMobile = useSelector((state) => state.service.isServiceMobile);
  const nameStickyService = useSelector((state) => state.service.nameStickyService);

  useEffect(() => {
    dispatch(setServiceDetailPage(true)); // Set isServiceDetailPage to true
  }, [dispatch]);


  const handleScroll = () => {
    const heightFromTop = window.scrollY;
    const bannerHeight = document.querySelector('.sub-header')?.offsetHeight || 0;

    // Make the heading sticky once the user scrolls past the banner
    // setNameSticky(heightFromTop > bannerHeight);
    dispatch(setNameStickyService(heightFromTop > bannerHeight));
  };

  useEffect(() => {
    // Detect if the screen size is mobile
    const updateScreenSize = () => {
      dispatch(setServiceMobile(window.innerWidth <= 599));
      // setIsServiceMobile(window.innerWidth <= 599);
    };

    // Initial check
    updateScreenSize();

    // Listen for window resize
    window.addEventListener('resize', updateScreenSize);

    return () => window.removeEventListener('resize', updateScreenSize);
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [dispatch, isServiceMobile]);

  useEffect(() => {
    // Fetch banner data
    const fetchBannerData = async () => {
      try {
        const query = `*[_type == "bannerImageSection" && page == "services"][0]{
          "imageUrl": bannerImage.asset->url,
          altText,
          heading
        }`;
        const data = await sanityClient.fetch(query);
        setBannerData(data);
      } catch (error) {
        console.error('Error fetching banner data:', error);
      }
    };

    fetchBannerData();
  }, []);

  useEffect(() => {
    // Fetch service data
    const fetchServiceData = async () => {
      try {
        const data = await sanityClient.fetch(
          `*[_type == "service"  && !defined(deletedAt) ]{
            title,
            serviceDetailHead,
            description,
            "imageUrl": bannerImage.asset->url,
            slug,
            servicedetailimage[] {
              asset->{url}
            },
            keypoints[]{
              title,
              description,
            },
            ourprocess[]{
              title,
              description,
            }
          }`
        );
        // console.log("Fetched Data:", data);
        const matchedService = data.find(item => item.slug.current.trim() === slug);
        if (matchedService) {
          setService(matchedService);
        } else {
          navigate("/something-went-wrong"); // Redirect to the 404 page if no match is found
        }
        // setService(matchedService || null);
        // {!service && (navigate("/404"))}
      } catch (error) {
        console.error('Error fetching service data:', error);
        navigate("/something-went-wrong");
      }
    };

    fetchServiceData();
  }, [slug]);

  useEffect(() => {
    // Fetch facilities data
    const fetchFacilitiesData = async () => {
      try {
        const data = await sanityClient.fetch(
          `*[_type == "facility"]{
            "imageUrl": image.asset->url
          }`
        );
        setFacilities(data);
      } catch (error) {
        console.error('Error fetching facilities data:', error);
      }
    };

    fetchFacilitiesData();
  }, []);

  const sliderSettings = {
    dots: false,
    arrows: false,
    slidesToShow: 4,
    infinite: true,
    autoplay: true,
    slidesToScroll: 1,
    pauseOnHover: false,
    cssEase: "linear",
    autoplaySpeed: 0,
    speed: 8000,
    responsive: [
      { breakpoint: 2561, settings: { slidesToShow: 5 } },
      { breakpoint: 1500, settings: { slidesToShow: 4 } },
      { breakpoint: 991, settings: { slidesToShow: 4 } },
      { breakpoint: 767, settings: { slidesToShow: 3 } },
      { breakpoint: 599, settings: { slidesToShow: 2 } },
    ],
  };

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "project"]{
          title,
          city,
          category->{_id, title},
          serviceRelatedProject->{_id, title},
          "imageUrl": smallListingPageImage.asset->url,
          slug
        }`
      )
      .then((data) => {
        // Filter projects based on matching titles
        let filteredProjects = data.filter(
          (project) =>
            project.serviceRelatedProject?.title === service?.title
        );

        // If less than 3 related projects, add more projects
        if (filteredProjects.length < 3) {
          const additionalProjects = data.filter(
            (project) =>
              !filteredProjects.includes(project) // Avoid duplicates
          );
          filteredProjects = [
            ...filteredProjects,
            ...additionalProjects.slice(0, 3 - filteredProjects.length), // Add only the required number
          ];
        }

        setRelatedProjects(filteredProjects);
      })
      .catch(console.error);
  }, [service?.title]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  if (!service) return null;

  return (
    <>

      <section className="sub-header project-sub">
        {service?.imageUrl && (<img src={service.imageUrl} className="img-responsive" alt="subhead" />)}
        <div className="subheader-text">
          <h1 className="heading">{service?.title}</h1>
        </div>
      </section>

      {isServiceMobile ? (nameStickyService &&
        (<section className="service-namefixed title-fixed">
          <h2 className="heading">{service?.title}</h2>
        </section>)
      ) : (
        <section className={`service-namefixed ${nameStickyService ? "title-fixed" : ""}`}>
          <h2 className="heading">{service?.title}</h2>
        </section>
      )}

      <section className="servicepage-head">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-9 wow animate__animated animate__slideInUp" data-wow-duration="1.2s" data-wow-delay="0.1s">
              {service?.serviceDetailHead ? (
                <PortableText value={service.serviceDetailHead} />
              ) : (
                <p></p>
              )}
              <Link to="/contact-us">Get in touch to learn more<span></span></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="facility-slider servicepage-slider">
        <Slider {...sliderSettings}>
          {facilities.map((facility, index) => (
            <img src={facility.imageUrl} key={index} className="img-responsive" alt="facility" loading="lazy" />
          ))}
        </Slider>
      </section>

      <ServicesDetail service={service} />
      <ServiceKeypoint service={service} />

      <section className="home-quality service-process">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 quality-grid">
              <div className="row quality-row">
                <div className="col-lg-12">
                  <div className="homequality-haed">
                    <h4 className="heading">our process</h4>
                    <img src="./assets/home/shape-vector.png" alt="vector" />
                  </div>
                </div>
                {service?.ourprocess?.map((process, index) => (
                  <div className="col-lg-4 col-md-4 wow animate__animated animate__slideInUp" data-wow-duration={`${1 + index * 0.1}s`} key={index}>
                    <div className="quality-box">
                      <h4>{`0${index + 1}`}</h4>
                      <span>{process.title}</span>
                      <p>{process.description}</p>
                      {/* <span>Initial Brainstorming</span>
                    <p>We begin with an in-depth consultation to fully understand your unique vision, personal preferences, and specific lifestyle needs, ensuring a tailored approach.</p> */}
                    </div>
                  </div>
                ))}

              </div>
            </div>
          </div>
        </div>
      </section>

      <ServicesOther relatedProjects={relatedProjects} />
      {loading ? null : <GetInTouch />}

    </>
  );
}
